import type { Agent } from '@/types/Agents';
import type { Conversation } from '@/types/Conversation';

/**
 * Why a conversation is or is not counted against the plan. `counts_toward_usage`
 * only tells us that it is excluded; the subscription window tells us why.
 */
export type ConversationBillingState =
  | 'billable'
  | 'before_subscription'
  | 'outside_subscription';

export type ConversationBillingFilter = 'all' | 'billable' | 'non_billable';

/** Conversations the agent handed to a person and nobody has picked up yet. */
export const needsCallback = (conversation: Conversation): boolean =>
  conversation.callback_requested === true;

/** Absent flag means billable: the column defaults to true and is set on insert. */
export const isBillableConversation = (conversation: Conversation): boolean =>
  conversation.counts_toward_usage !== false;

export const getConversationBillingState = (
  conversation: Conversation,
  subscription?: Agent['subscription']
): ConversationBillingState => {
  if (isBillableConversation(conversation)) return 'billable';

  const activatedAt = subscription?.activated_at;
  if (!activatedAt) return 'before_subscription';

  return new Date(conversation.created_at) < new Date(activatedAt)
    ? 'before_subscription'
    : 'outside_subscription';
};

export const matchesBillingFilter = (
  conversation: Conversation,
  filter: ConversationBillingFilter
): boolean => {
  if (filter === 'all') return true;
  const billable = isBillableConversation(conversation);
  return filter === 'billable' ? billable : !billable;
};
